import type { VoiceState } from "../voice/VoiceChat";
import { requireElement } from "./Hud";

/**
 * Na area aberta quem decide ouvir so quem esta perto e o navegador de cada
 * um: alguem com um cliente modificado ouve a area aberta inteira. O jogador
 * precisa saber disso (para assunto reservado: sala do mapa ou trancar).
 */
export const OPEN_NOT_PRIVATE =
  "Na área aberta a conversa não é privada: alguém longe pode estar ouvindo. " +
  "Para um assunto reservado, use uma sala de voz do mapa ou tranque a conversa.";

/** Por quanto tempo um pedido para entrar na conversa fica na tela. */
const KNOCK_MS = 30000;

/** A acao do botao ao lado do status, decidida pela cena a cada quadro. */
export type VoiceAction = { kind: "lock" } | { kind: "unlock" } | { kind: "knock"; locked: string } | null;

/**
 * Painel do chat de voz: botao do microfone (M), em que sala o jogador esta
 * e o botao de trancar/destrancar a conversa ou pedir para entrar numa
 * trancada. Os pedidos de quem quer entrar aparecem como cartoes acima dele.
 */
export class VoicePanel {
  private readonly root = requireElement<HTMLDivElement>("voice");
  private readonly micBtn = requireElement<HTMLButtonElement>("voice-mic");
  private readonly labelEl = requireElement<HTMLSpanElement>("voice-label");
  private readonly detailEl = requireElement<HTMLSpanElement>("voice-detail");
  private readonly infoEl = requireElement<HTMLDivElement>("voice-info");
  private readonly actionBtn = requireElement<HTMLButtonElement>("voice-action");
  private readonly knocksEl = requireElement<HTMLDivElement>("voice-knocks");

  private action: VoiceAction = null;
  private actionKey = "";
  private readonly knocks = new Map<string, { el: HTMLElement; timer: number }>();

  private micHandler?: () => void;
  private actionHandler?: (action: Exclude<VoiceAction, null>) => void;
  private admitHandler?: (id: string) => void;

  constructor() {
    // blur: com o foco no botao, Espaco/Enter (interagir, chat) o clicariam de novo
    this.micBtn.addEventListener("click", () => {
      this.micBtn.blur();
      this.micHandler?.();
    });
    this.actionBtn.addEventListener("click", () => {
      this.actionBtn.blur();
      if (this.action) this.actionHandler?.(this.action);
    });
  }

  onMic(handler: () => void): void {
    this.micHandler = handler;
  }

  onAction(handler: (action: Exclude<VoiceAction, null>) => void): void {
    this.actionHandler = handler;
  }

  onAdmit(handler: (id: string) => void): void {
    this.admitHandler = handler;
  }

  setAvailable(available: boolean): void {
    this.root.classList.toggle("hidden", !available);
    if (!available) this.clearKnocks();
  }

  setState(state: VoiceState): void {
    const { mic, status } = state;
    this.micBtn.classList.toggle("on", mic);
    this.micBtn.setAttribute("aria-pressed", String(mic));
    this.micBtn.title = mic ? "Desligar o microfone (M)" : "Ligar o microfone (M)";

    this.root.dataset.kind = state.kind;
    this.labelEl.textContent = state.label || "Voz";
    this.infoEl.title = state.kind === "open" ? OPEN_NOT_PRIVATE : "";
    let detail: string;
    if (status === "connecting") detail = "conectando…";
    else if (status === "error") detail = "sem conexão de voz";
    else if (state.needsGesture) detail = "toque na tela para ouvir";
    else if (state.kind === "open") detail = mic ? "perto ouve você · não é privada" : "microfone desligado · não é privada";
    else detail = state.peers === 0 ? "só você por aqui" : `${state.peers + 1} pessoas`;
    this.detailEl.textContent = detail;
    this.root.classList.toggle("problem", status === "error" || state.needsGesture);
  }

  /** Mostra (ou esconde, com null) a acao possivel agora. */
  setAction(action: VoiceAction): void {
    const key = action ? action.kind + (action.kind === "knock" ? action.locked : "") : "";
    if (key === this.actionKey) return;
    this.actionKey = key;
    this.action = action;
    this.actionBtn.classList.toggle("hidden", !action);
    if (!action) return;
    const text = { lock: "🔒 Trancar conversa", unlock: "🔓 Destrancar", knock: "🚪 Pedir para entrar" }[action.kind];
    const title = {
      lock: "Só quem está conversando com você continua se ouvindo",
      unlock: "Abrir a conversa para quem chegar perto",
      knock: "Esta conversa está trancada: peça para entrar",
    }[action.kind];
    this.actionBtn.textContent = text;
    this.actionBtn.title = title;
  }

  /** Alguem bateu na conversa trancada: cartao com "Deixar entrar". */
  addKnock(id: string, name: string): void {
    this.removeKnock(id);
    const card = document.createElement("div");
    card.className = "voice-knock";
    const text = document.createElement("span");
    text.textContent = `${name} quer entrar na conversa`;
    const admit = document.createElement("button");
    admit.type = "button";
    admit.className = "accent";
    admit.textContent = "Deixar entrar";
    admit.addEventListener("click", () => {
      this.removeKnock(id);
      this.admitHandler?.(id);
    });
    const dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.textContent = "✕";
    dismiss.setAttribute("aria-label", "Ignorar");
    dismiss.addEventListener("click", () => this.removeKnock(id));
    card.append(text, admit, dismiss);
    this.knocksEl.append(card);
    this.knocks.set(id, { el: card, timer: window.setTimeout(() => this.removeKnock(id), KNOCK_MS) });
  }

  private removeKnock(id: string): void {
    const knock = this.knocks.get(id);
    if (!knock) return;
    window.clearTimeout(knock.timer);
    knock.el.remove();
    this.knocks.delete(id);
  }

  clearKnocks(): void {
    for (const id of [...this.knocks.keys()]) this.removeKnock(id);
  }
}
